/**
 * reviewOfSystems.js — v242.127.0 · ROS-FABRICATED-NEGATIVE-1
 *
 * ★★★ WHAT THIS FILE EXISTS TO STOP: A SYSTEM NOBODY ASKED ABOUT WAS PRINTING
 *   AS NORMAL, INTO A CLINICAL DOCUMENT.
 *
 * The old generateROS() lived inside MyMedicalNote and had three branches. The
 * third one was the defect:
 *
 *     if (matchedSymptoms.length > 0)  -> "Positive for <symptoms>"
 *     else if (isSpecialtySystem)      -> "See HPI"
 *     else                             -> system.default          <-- ★
 *
 * `system.default` is a sentence like "No cough, wheezing, or shortness of
 * breath". In a real review of systems that sentence means A CLINICIAN ASKED AND
 * THE PATIENT DENIED IT. Nothing here asked. The patient answered a
 * specialty-scoped signs_symptoms questionnaire; the other nine systems were
 * never enumerated, so every one of them was reported as a pertinent negative
 * that was never elicited.
 *
 * ★★ AND IT WAS TOTAL IN SPANISH. `matchedSymptoms` is
 * `symptomLower.includes(keyword)` against ENGLISH keyword lists, run over the
 * patient's own answer text. A Spanish speaker's answers contain no English
 * keyword, so nothing matched, so ALL TEN systems fell to the default and the
 * note asserted a completely normal review for a patient who had reported
 * symptoms.
 *
 * ★ THE HEADING WAS HALF THE DAMAGE. "Review of Systems" is a term of art: it
 * means asked and answered. Under that heading an omission reads as a claim.
 * The section is now "Symptoms reported by system", which is what the data
 * actually is, and an unmatched system is simply ABSENT rather than negative.
 *
 * ★★★ MOVE 2, v242.165.0 — ROS-KEYWORD-MATCH-1 IS CLOSED. The matcher is gone.
 * It read the patient's ANSWER TEXT through 73 English keywords with
 * `includes()`, which is a substring test, so "No chest pain" contained
 * "chest pain" and one denial produced THREE positives — Respiratory,
 * Cardiovascular and Musculoskeletal — while "Sin dolor de pecho" produced none.
 * ★★ THE SYSTEM IS NOW A PROPERTY OF THE QUESTION (utils/rosSystems.js), which is
 *    authored once and has an ID. The answer decides only WHETHER the question
 *    was answered. A denial and an affirmation both mean "this question was
 *    asked and answered" — which is exactly what a reported-symptoms list should
 *    say — and neither can invent a system, in any language.
 * ★ THE SEE-HPI BRANCH WAS BROKEN THE SAME WAY AND NOBODY HAD NOTICED. It ran
 *   `specialtyName.toLowerCase().includes('ear')` against the DISPLAY NAME, so
 *   "Otorrinolaringología" matched nothing and a Spanish case lost its own
 *   specialty's heading too. It keys off specialtyId now, which is not
 *   translated.
 *
 * Pure on purpose — no React, no store, no closure over currentCase — so
 * scripts/check-ros-negatives.mjs can EXECUTE it. A decision that cannot be run
 * cannot be gated (ORELLA_RULES §1cd(f)).
 */
// ★ THE `.js` IS LOAD-BEARING. check-ros-negatives.mjs EXECUTES this module with
//   node, and node's ESM resolver does not add extensions — Vite does. Drop it
//   and the gate dies with ERR_MODULE_NOT_FOUND while the app builds fine, which
//   is a red build for a reason that has nothing to do with the app.
import { SPECIALTY_SYSTEM, systemForQuestion } from './rosSystems.js'

// No `default` field. There is deliberately nowhere left to put one.
// ★ The keyword lists are GONE. They were the matcher, and the matcher was the
//   defect; a list of English nouns cannot decide what a Spanish sentence is
//   about, and `includes()` cannot tell a denial from a report. What remains is
//   the ORDER systems print in — nothing reads these as evidence any more.
export const ROS_SYSTEMS = [
  { name: 'General' },
  { name: 'HEENT' },
  { name: 'Respiratory' },
  { name: 'Cardiovascular' },
  { name: 'Gastrointestinal' },
  { name: 'Genitourinary' },
  { name: 'Musculoskeletal' },
  { name: 'Skin' },
  { name: 'Neurological' },
  { name: 'Psychiatric' },
]

/**
 * The patient's answered signs_symptoms questions, each carrying the system its
 * QUESTION belongs to. The answer's text is kept only for display.
 * ★ An answer whose question resolves to no system is dropped here rather than
 *   guessed at — absent is not a claim.
 */
export function extractReportedAnswers(answers, specialtyId, focusAreaId) {
  const out = []
  Object.entries(answers || {}).forEach(([key, value]) => {
    if (!key.startsWith('signs_symptoms_') || !value) return
    const questionId = key.slice('signs_symptoms_'.length)
    const system = systemForQuestion(specialtyId, focusAreaId, questionId)
    if (!system) return
    const texts = Array.isArray(value)
      ? value.filter(v => typeof v === 'string' && v.trim())
      : (typeof value === 'string' && value.trim() ? [value] : [])
    for (const t of texts) out.push({ questionId, system, text: t })
  })
  return out
}

/**
 * Returns ONLY the systems this case has something to say about.
 *   { system, kind: 'positive', symptoms: [...] }   the patient answered here
 *   { system, kind: 'see_hpi' }                     the case's own specialty
 * A system with neither is ABSENT. It is not negative, because it was not asked.
 */
export function buildReportedSystems(reportedAnswers, specialtyId) {
  const entries = []
  const own = SPECIALTY_SYSTEM[specialtyId] || null
  for (const system of ROS_SYSTEMS) {
    const matched = (reportedAnswers || [])
      .filter(a => a && a.system === system.name && typeof a.text === 'string' && a.text.trim())
      .map(a => a.text)
    if (matched.length > 0) entries.push({ system: system.name, kind: 'positive', symptoms: matched })
    else if (own === system.name) entries.push({ system: system.name, kind: 'see_hpi' })
  }
  return entries
}

/** Display lines. Never emits a negative — there is no branch that could.
 * ★ "Reported" means the question was asked and answered. It does NOT mean the
 *   answer was positive, and it never did — the text is shown so a reader can
 *   see what the patient actually said, denials included. */
export function formatReportedSystems(entries) {
  return (entries || []).map(e =>
    e.kind === 'positive'
      ? `• ${e.system}: Reported — ${e.symptoms.join(', ').toLowerCase()}`
      : `• ${e.system}: See HPI`
  )
}
